import type { Plane, FireCell } from '@firesim/simkit-core';
import type { TerrainCodes } from '../terrain-types';

export interface RegionSelection {
	top: boolean;
	left: boolean;
	startX: number;
	startY: number;
	endX: number;
	endY: number;
}

export interface SelectRegionOptions<T extends string | number = TerrainCodes> {
	plane: Plane<T, FireCell<T>>;
}

function selectRegion<T extends string | number = TerrainCodes>(
	callback: (region: RegionSelection) => void,
	options: SelectRegionOptions<T>
): void {
	const plane = options.plane;

	let active: Partial<RegionSelection> = {};

	function clearActive() {
		// clear old setting
		if(active.top !== undefined) {
			plane.clearRect(
				active.left ? 0 : plane.width / 2,
				active.top ? 0 : plane.height / 2,
				plane.width / 2,
				plane.height / 2,
				'effects'
			);
		}
	}

	function moveHandler(evt: { x: number; y: number }) {
		const top = evt.y < plane.height / 2;
		const left = evt.x < plane.width / 2;

		if(active.top !== top || active.left !== left) {
			clearActive();

			// Draw rectangle
			plane.drawRect(
				left ? 0 : plane.width / 2,
				top ? 0 : plane.height / 2,
				plane.width / 2,
				plane.height / 2,
				'rgba(0, 0, 0, 0.5)',
				'effects'
			);

			active = {top, left};
		}
	}

	function clickHandler() {
		plane.removeEventListener('mousemove', moveHandler);
		plane.removeEventListener('click', clickHandler);
		clearActive();
		const callbackObject: RegionSelection = {
			top: active.top ?? false,
			left: active.left ?? false,
			startX: active.left ? 0 : Math.floor(plane.width / 2),
			startY: active.top ? 0 : Math.floor(plane.height / 2),
			endX: active.left ? Math.floor(plane.width / 2) : plane.width,
			endY: active.top ? Math.floor(plane.height / 2) : plane.height
		};
		callback(callbackObject);
	}

	plane.addEventListener('mousemove', moveHandler);
	plane.addEventListener('click', clickHandler);
}

export default selectRegion;
